/*
 *	Copyright (c) 2024, Signaloid.
 *
 *	Permission is hereby granted, free of charge, to any person obtaining a copy
 *	of this software and associated documentation files (the "Software"), to deal
 *	in the Software without restriction, including without limitation the rights
 *	to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 *	copies of the Software, and to permit persons to whom the Software is
 *	furnished to do so, subject to the following conditions:
 *
 *	The above copyright notice and this permission notice shall be included in all
 *	copies or substantial portions of the Software.
 *
 *	THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 *	IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 *	FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 *	AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 *	LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 *	OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 *	SOFTWARE.
 */

import { createApp } from 'vue'
import App from '@/App.vue'
import router from '@/router'
import vuetify from '@/plugins/vuetify'
import hljsVuePlugin from '@highlightjs/vue-plugin'
import 'highlight.js/styles/stackoverflow-light.css'
import 'highlight.js/lib/common'
import { createPinia } from 'pinia'
import { SnackbarService } from 'vue3-snackbar'
import * as Sentry from '@sentry/vue'
import 'vue3-snackbar/styles'

const app = createApp(App)

Sentry.init({
	app,
	dsn: 'https://d7544e77028f4de48d958d29e100fa13@o4504621548896256.ingest.us.sentry.io/4505504213762048',
	integrations: [Sentry.browserTracingIntegration({ router }), Sentry.replayIntegration()],
	tracesSampleRate: 1.0,
	denyUrls: ['localhost'],
	replaysSessionSampleRate: 0.1,
	normalizeDepth: 10,
	environment: import.meta.env.VITE_ENV,
	replaysOnErrorSampleRate: 1.0,
	beforeBreadcrumb: (breadcrumb, hint) => {
		if (breadcrumb.category === 'xhr' && hint?.xhr) {
			const data = {
				requestBody: hint.xhr.__sentry_xhr__.body,
				response: hint.xhr.response,
				responseUrl: hint.xhr.responseURL
			}
			return { ...breadcrumb, data }
		}
		return breadcrumb
	},
	enabled: import.meta.env.VITE_ENV !== 'development'
})

const pinia = createPinia()

app.use(router).use(vuetify).use(SnackbarService).use(hljsVuePlugin).use(pinia).mount('#app')
